import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { campaignsClient } from 'clients/CampaignsClient/CampaignsClient';
import { queryClient } from 'config/queryClient';
import { usePageContext } from 'hooks/shared/usePageContext';
import { PageContext } from 'hooks/shared/usePageContext.types';
import { QueryKey } from 'types/query';
import { facetsInitialState } from './SelectedCampaignContext.context';
import { CampaignFacets } from './SelectedCampaignContext.types';

export const useFacets = (id: number | undefined) => {
  const pageContext = usePageContext();

  const { data, isFetching } = useQuery<CampaignFacets>(
    [QueryKey.selectedCampaignContextUseFacets, id],
    async () => {
      if (id === undefined) throw Error('Campaign id undefined');

      const { data } = await campaignsClient.getFacets(id);

      const { buckets } = data.aggregations.source_facets.source_facet;

      return buckets.reduce((acc, { key, doc_count }) => ({ ...acc, [key]: doc_count }), facetsInitialState);
    },
    { enabled: id !== undefined && pageContext === PageContext.Campaign },
  );

  return useMemo(() => ({ facets: data || facetsInitialState, isLoading: isFetching }), [data, isFetching]);
};

export const setFacets = (id: number | undefined, callback: (data: CampaignFacets) => CampaignFacets) => {
  queryClient.setQueryData([QueryKey.selectedCampaignContextUseFacets, id], (data: CampaignFacets | undefined) => {
    if (!data) throw Error('Set facets undefined data');

    return callback(data);
  });
};
